import React, { useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";

import { cartSelector } from "../../redux/cart/selectors";
import { setCartItems } from "../../redux/cart/slice";
import { useAppDispatch, useAppSelector } from "../../redux/store";

import { Header, Title } from "../../components";
import { AcceptTermsForm, RacketToggleCard } from "./components";

import {
  addCurrencyWord,
  getCartItemsFromStorage,
  setTotalPriceToStorage,
  getJustPaidFlag,
} from "../../utils";

import styles from "./Cart.module.scss";

import { CartPropsType, IRacket, ISeat, IProduct, ITotalCartItems } from "./types";
import { appController } from "../../resources/constants";
import { useLocation, useNavigate } from "react-router-dom";
import { openModal } from "react-url-modal";
import { setShowWindow } from "../../redux/profile/authAndReg/slice";
import { WindowNames } from "../../resources/constants";
import { checkExpiredCourtItems, sortCourts } from "../../utils/payment/cancelExpiredItems";
import { updateCartItems, createDeleteItemsPayload, getItemStartTime, isItemExpired } from "../../utils/cart/cartHelpers";
import { createAttachedRacket, generateRacketKey, isRacketAttachedToCourt, isRacketForCourt } from "../../utils/cart/racketHelpers";
import { filterExpiredRackets, shouldDisableRacketToggle } from "../../utils/cart/expiredItemsHelpers";
import { renderCartItems } from "../../utils/cart/renderCartItems";
import { EmptyBasketCart } from "./components/EmptyBasketCart/EmptyBasketCart";
import { CartSkeleton } from "./components/CartSkeleton";

export const Cart: React.FC<CartPropsType> = ({ title }) => {
  const { totalCartItems, fetchReserveStatus, isLoading } = useSelector(cartSelector);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { auth } = useAppSelector(state => state.authAndReg);

  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    if (!auth) {
      navigate("/", { replace: true });
      openModal({ name: "login", params: { showWindow: "auth" } });
      dispatch(setShowWindow(WindowNames.AUTH));
    }
  }, [auth, navigate, dispatch]);

  React.useEffect(() => {
    const storageCartItems = getCartItemsFromStorage();
    if (storageCartItems) dispatch(setCartItems(storageCartItems));
  }, []);

  React.useEffect(() => {
    if (getJustPaidFlag()) {
      return;
    }

    const checkExpiredItems = async () => {
      const courtRent = totalCartItems.seats || {};
      const racketRent = totalCartItems.rackets || {};

      const validCourts = await checkExpiredCourtItems(courtRent as any, dispatch);
      const validRackets = filterExpiredRackets(racketRent as { [key: string]: IRacket }, validCourts);

      const hasChanges =
        Object.keys(validCourts).length !== Object.keys(courtRent).length ||
        Object.keys(validRackets).length !== Object.keys(racketRent).length;

      if (hasChanges) {
        const updatedCartItems = {
          ...totalCartItems,
          seats: validCourts,
          rackets: validRackets,
        };

        updateCartItems(updatedCartItems, dispatch);

        if (shouldDisableRacketToggle(updatedCartItems)) {
          setRacketsEnabled(false);
        }
      }
    };

    checkExpiredItems();

    const checkExpiredTimer = setInterval(() => {
      checkExpiredItems();
    }, 5000);

    return () => {
      clearInterval(checkExpiredTimer);
    };
  }, [totalCartItems, dispatch]);

  React.useEffect(() => {
    deleteItemIfAfk(totalCartItems, appController.AFK_TIMER_MINUTES);

    const testToDeleteItemsTimer = setInterval(() => {
      deleteItemIfAfk(totalCartItems, appController.AFK_TIMER_MINUTES);
    }, 5000);

    return () => {
      clearInterval(testToDeleteItemsTimer);
    };
  }, [totalCartItems]);


  const sortedCourts = useMemo(() => {
    const courtRent = totalCartItems.seats || {};
    return sortCourts(courtRent as any);
  }, [totalCartItems.seats]);

  const totalPriceCalculated = useMemo(() => {
    let total = 0;
    
    sortedCourts.forEach((seat: ISeat) => {
      total += seat.price || 0;
    });

    const racketRent = totalCartItems.rackets || {};
    Object.values(racketRent).forEach((racket) => {
      const racketItem = racket as IRacket;
      total += (racketItem.price || 0) * (racketItem.count || 0);
    });

    const productsInBasket = totalCartItems.products || {};
    Object.values(productsInBasket).forEach((product) => {
      const productItem = product as IProduct;
      total += (productItem.price || 0) * (productItem.count || 1);
    });

    const storageItems = totalCartItems.storage || {};
    Object.values(storageItems).forEach((item: any) => {
      total += (item.price || 0) * (item.count || 1);
    });

    setTotalPriceToStorage(total);
    return addCurrencyWord(total);
  }, [sortedCourts, totalCartItems.rackets, totalCartItems.products, totalCartItems.storage]);

  const getRacketsFromToggle = React.useCallback(() => {
    const rackets = totalCartItems.rackets || {};
    return Object.values(rackets).filter((racket: any) => 
      isRacketAttachedToCourt(racket as IRacket)
    ) as IRacket[];
  }, [totalCartItems.rackets]);

  const [racketsEnabled, setRacketsEnabled] = React.useState(() => {
    return getRacketsFromToggle().length > 0;
  });

  const handleRacketToggle = React.useCallback((enabled: boolean) => {
    setRacketsEnabled(enabled);
    
    const cloneTotalCartItems = JSON.parse(JSON.stringify(totalCartItems));
    const seats = cloneTotalCartItems.seats || {};
    const rackets = cloneTotalCartItems.rackets || {};

    if (enabled) {
      Object.keys(seats).forEach((key) => {
        const seat = seats[key] as ISeat;
        const racketKey = generateRacketKey(seat);
        
        if (!rackets[racketKey]) {
          rackets[racketKey] = createAttachedRacket(seat);
        }
      });
    } else {
      Object.keys(rackets).forEach((key) => {
        const racket = rackets[key] as IRacket;
        if (isRacketAttachedToCourt(racket)) {
          delete rackets[key];
        }
      });
    }

    cloneTotalCartItems.rackets = rackets;
    updateCartItems(cloneTotalCartItems, dispatch);
  }, [totalCartItems, dispatch]);

  React.useEffect(() => {
    const toggleRackets = getRacketsFromToggle();
    setRacketsEnabled(toggleRackets.length > 0);
  }, [totalCartItems.rackets, totalCartItems.seats]);

  const isCartEmpty = testCartEmpty(totalCartItems);
  
  const hasCourts = Object.keys(totalCartItems.seats || {}).length > 0;

  const deleteItemIfAfk = (cartItems: ITotalCartItems, minutes: number) => {
    const cloneCartItems = JSON.parse(JSON.stringify(cartItems));

    const filtredCartItems: {
      [key: string]: { [key: string]: ISeat | IRacket | IProduct };
    } = {
      rackets: { ...cloneCartItems.rackets },
      seats: { ...cloneCartItems.seats },
      products: { ...cloneCartItems.products },
    };

    const deletedCartItems: {
      rackets: { [key: string]: IRacket };
      seats: { [key: string]: ISeat };
      products: { [key: string]: IProduct };
    } = {
      rackets: {},
      seats: {},
      products: {},
    };

    // Обрабатываем корты и ракетки
    const typesToProcess: Array<'seats' | 'rackets'> = ['seats', 'rackets'];
    
    typesToProcess.forEach((type) => {
      const items = cartItems[type] || {};
      
      Object.keys(items).forEach((itemKey) => {
        const item = items[itemKey] as ISeat | IRacket;
        const startTime = getItemStartTime(item, type);

        if (isItemExpired(startTime, minutes)) {
          // Удаляем элемент
          const filtredItems = Object.keys(filtredCartItems[type])
            .filter((key) => {
              const currentItem = items[key] as ISeat | IRacket;
              return currentItem.id !== item.id;
            })
            .reduce((res: { [key: string]: ISeat | IRacket }, key) => {
              res[key] = items[key] as ISeat | IRacket;
              return res;
            }, {});

          filtredCartItems[type] = filtredItems;
          deletedCartItems[type][itemKey] = item;

          // Если удаляется корт, удаляем связанные ракетки
          if (type === 'seats') {
            const deletedSeat = item as ISeat;
            const rackets = filtredCartItems.rackets || {};
            
            Object.keys(rackets).forEach((racketKey) => {
              const racket = rackets[racketKey] as IRacket;
              if (isRacketForCourt(racket, deletedSeat)) {
                deletedCartItems.rackets[racketKey] = racket;
                delete rackets[racketKey];
              }
            });
            
            filtredCartItems.rackets = rackets;
          }
        }
      });
    });

    const hasDeletedItems =
      Object.keys(deletedCartItems.rackets).length > 0 ||
      Object.keys(deletedCartItems.seats).length > 0;

    if (hasDeletedItems) {
      const deletePayload = createDeleteItemsPayload(
        deletedCartItems.seats,
        deletedCartItems.rackets,
        deletedCartItems.products
      );

      updateCartItems(filtredCartItems as ITotalCartItems, dispatch);

      if (shouldDisableRacketToggle(filtredCartItems as ITotalCartItems)) {
        setRacketsEnabled(false);
      }
    }
  };

  const handleDeleteProduct = useCallback((productId: number) => {
    const cloneTotalCartItems = JSON.parse(JSON.stringify(totalCartItems));
    const productsInCart = cloneTotalCartItems.products || {};
    const productKey = productId.toString();
    
    const deletedProduct = productsInCart[productKey];
    delete productsInCart[productKey];

    cloneTotalCartItems.products = productsInCart;

    const deletePayload = createDeleteItemsPayload(
      {},
      {},
      { [productKey]: deletedProduct }
    );

    updateCartItems(cloneTotalCartItems, dispatch);
  }, [totalCartItems, dispatch]);

  const handleUpdateProductQuantity = useCallback((productId: number, newCount: number) => {
    const cloneTotalCartItems = JSON.parse(JSON.stringify(totalCartItems));
    const productsInCart = cloneTotalCartItems.products || {};
    const product = productsInCart[productId.toString()] as IProduct;
    
    if (product) {
      product.count = newCount;
      updateCartItems(cloneTotalCartItems, dispatch);
    }
  }, [totalCartItems, dispatch]);

  const handleIncreaseProductQuantity = useCallback((productId: number) => {
    const cloneTotalCartItems = JSON.parse(JSON.stringify(totalCartItems));
    const productsInCart = cloneTotalCartItems.products || {};
    const product = productsInCart[productId.toString()] as IProduct;
    
    if (product) {
      const newCount = (product.count || 1) + 1;
      handleUpdateProductQuantity(productId, newCount);
    }
  }, [totalCartItems, handleUpdateProductQuantity]);

  const handleDecreaseProductQuantity = useCallback((productId: number) => {
    const cloneTotalCartItems = JSON.parse(JSON.stringify(totalCartItems));
    const productsInCart = cloneTotalCartItems.products || {};
    const product = productsInCart[productId.toString()] as IProduct;
    
    if (product) {
      const currentCount = product.count || 1;
      
      if (currentCount > 1) {
        handleUpdateProductQuantity(productId, currentCount - 1);
      } else {
        handleDeleteProduct(productId);
      }
    }
  }, [totalCartItems, handleUpdateProductQuantity, handleDeleteProduct]);

  const cartItemsElements = useMemo(() => {
    return renderCartItems({
      cartItems: totalCartItems,
      onDeleteProduct: handleDeleteProduct,
      onIncreaseProduct: handleIncreaseProductQuantity,
      onDecreaseProduct: handleDecreaseProductQuantity,
    });
  }, [totalCartItems, handleDeleteProduct, handleIncreaseProductQuantity, handleDecreaseProductQuantity]);

  function testCartEmpty(cartItems: ITotalCartItems) {
    return !!(
      Object.keys(cartItems.rackets || {}).length ||
      Object.keys(cartItems.seats || {}).length ||
      Object.keys(cartItems.products || {}).length ||
      Object.keys(cartItems.storage || {}).length
    );
  }

  return (
    <>
      <Header title={title} />
      <main>
        <section className="container">
          <Title>{title}</Title>

          {isLoading ? (
            <CartSkeleton />
          ) : fetchReserveStatus && fetchReserveStatus !== "Success" ? (
            <div className={styles.cartItems}>
              Произошла ошибка! Возможно проблемы с интернет соединением. Попробуйте позже
              или свяжитесь с нами.
            </div>
          ) : isCartEmpty ? (
            <div className={styles.cartWrapper}>
              <div className={styles.cartItems}>
                {hasCourts && (
                  <RacketToggleCard
                    checked={racketsEnabled}
                    onToggle={handleRacketToggle}
                  />
                )}
                {cartItemsElements}
              </div>
              <AcceptTermsForm totalPrice={totalPriceCalculated} />
            </div>
          ) : (
            <EmptyBasketCart />
          )}
        </section>
      </main>
    </>
  );
};
